// Единый экран «Смарт-процессы Bitrix»: 20 типов, дозеркаленных 31.07.2026, одним
// разделом — слева реестр со счётами, справа записи выбранного типа из crm_items.
//
// ПОЧЕМУ ОДИН ЭКРАН, А НЕ ДВАДЦАТЬ. Довод тот же, что у /smart/* (SmartProcess.tsx):
// читаются типы совершенно одинаково, различается только состав UF-полей, а его
// нельзя зашивать — коды у каждого типа свои и меняются в Bitrix без нас. Отдельным
// экраном ушли бы только типы, у которых есть РАСЧЁТ (как дебиторка), — таких среди
// двадцати нет.
//
// ЧЕМ ОТЛИЧАЕТСЯ ОТ /smart/*. Там пять типов с давно налаженным справочником полей и
// фильтрами по нему; здесь — двадцать свежих, у которых статистика заполненности в
// справочнике ещё не считалась. Поэтому колонки подбираются по ЖИВЫМ строкам первой
// страницы (raw приезжает с ней), а подписи — из той же ручки /api/fields, что у
// всех: не пришёл справочник — колонки честно подписаны кодами, а не спрятаны.
//
// Экран ТОЛЬКО ЧИТАЕТ, и это не «пока не успели»: записи этих типов наполняют
// бизнес-процессы Bitrix, запись перезапускала бы согласования и раздавала задания
// живым людям.

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useFields } from '../api/hooks'
import type { СтрокаТипа, ТипРеестра } from '../api/bxSmart'
import {
  РАЗМЕР_СТРАНИЦЫ, useЗапись, useРеестр, useСтраница, выбратьКолонки, показатьЗначение,
  пусто,
} from '../api/bxSmart'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, pluralN } from '../lib/format'

export default function BxSmart() {
  const [sp, setSp] = useSearchParams()
  // Тип живёт в адресе: ссылку «посмотри реестр УПД» должно быть можно переслать.
  const типСтрокой = sp.get('type') || ''
  const тип = /^\d+$/.test(типСтрокой) ? Number(типСтрокой) : null

  const реестр = useРеестр()
  const типы = реестр.data?.rows ?? []
  const текущий = типы.find((т) => т.type === тип)

  const выбрать = (т: number) => {
    const next = new URLSearchParams(sp)
    next.set('type', String(т))
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Смарт-процессы Bitrix</h1>
        <span className="muted">
          {типы.length > 0 ? `типов: ${типы.length}` : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hint">
          Двадцать типов, дозеркаленных 31.07.2026 по своду аудита. Раздел только
          читает зеркало: записи этих типов наполняют бизнес-процессы Bitrix, и запись
          отсюда перезапускала бы согласования. У семи старых типов (дебиторка, счета,
          договоры, АВР…) свои разделы — здесь их нет намеренно.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: '1 1 280px', minWidth: 260, maxWidth: 380 }}>
          <h2 style={{ marginTop: 0 }}>Реестр типов</h2>
          <QueryState isLoading={реестр.isLoading} error={реестр.error}>
            <Реестр типы={типы} выбранный={тип} выбрать={выбрать} />
          </QueryState>
        </div>

        <div style={{ flex: '10 1 480px', minWidth: 0 }}>
          {тип === null ? (
            <div className="card">
              <div className="muted">
                Выберите тип слева — покажем его записи из зеркала.
              </div>
            </div>
          ) : (
            // key — чтобы при переходе между типами не «переехали» чужие поиск,
            // страницы и раскрытая карточка (приём из SmartProcess).
            <ТаблицаТипа key={тип} тип={тип} название={текущий?.title} />
          )}
        </div>
      </div>
    </>
  )
}

function Реестр({ типы, выбранный, выбрать }: {
  типы: ТипРеестра[]
  выбранный: number | null
  выбрать: (т: number) => void
}) {
  if (типы.length === 0) {
    return <div className="muted">Реестр из ядра не пришёл.</div>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {типы.map((т) => {
        const активен = т.type === выбранный
        return (
          <button key={т.type} type="button" onClick={() => выбрать(т.type)}
                  aria-pressed={активен}
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 8,
                    textAlign: 'left', width: '100%',
                    fontWeight: активен ? 600 : 400,
                  }}
                  title={`Смарт-процесс ${т.type}`}>
            <span>{т.title}</span>
            <span className="muted">{т.count}</span>
          </button>
        )
      })}
    </div>
  )
}

function ТаблицаТипа({ тип, название }: { тип: number; название?: string }) {
  const [ввод, setВвод] = useState('')
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)
  const [открытId, setОткрытId] = useState<number | null>(null)

  const f = useFields(`item:${тип}`)
  const q = useСтраница(тип, поиск, start)

  // «Показать ещё» копит строки: страница ядра — 50, а человек листает вниз.
  const [строки, setСтроки] = useState<СтрокаТипа[]>([])
  useEffect(() => {
    const д = q.data
    if (!д || д.type !== тип) return
    setСтроки((прежние) => (д.start === 0 ? д.rows : [...прежние, ...д.rows]))
  }, [q.data, тип])

  const искать = (значение: string) => {
    setПоиск(значение.trim())
    setStart(0)
    setОткрытId(null)
  }

  // Колонки — по заполненности ПЕРВОЙ страницы (raw в ней есть), не всего накопленного:
  // иначе состав колонок менялся бы под руками при каждом «показать ещё».
  const колонки = useMemo(() => выбратьКолонки(строки.slice(0, РАЗМЕР_СТРАНИЦЫ)), [строки])

  const data = q.data
  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>{название || `Смарт-процесс ${тип}`}</h2>
          <div className="spacer" />
          <span className="muted">
            {data ? `записей: ${data.total}` : ''}
          </span>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); искать(ввод) }}
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}
        >
          <div className="f" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="bxs-q">Название содержит</label>
            <input id="bxs-q" value={ввод} onChange={(e) => setВвод(e.target.value)} />
          </div>
          <button type="submit" disabled={q.isFetching}>Найти</button>
          {поиск && (
            <button type="button" onClick={() => { setВвод(''); искать('') }}>
              сбросить
            </button>
          )}
        </form>

        {!f.loaded && (
          <div className="hint" style={{ marginTop: 10 }}>
            Справочник полей смарт-процесса {тип} из ядра не пришёл — колонки с полями
            типа подписаны кодами Bitrix, а не названиями.
          </div>
        )}
      </div>

      <QueryState isLoading={q.isLoading} error={q.error}>
        <div className="card">
          {строки.length === 0 ? (
            <div className="empty">
              <div className="empty-title">
                {поиск ? 'Ничего не найдено под этот поиск' : 'Записей этого типа в зеркале нет'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Название</th>
                    <th>Стадия</th>
                    {колонки.map((к) => (
                      <th key={к} title={к}>{f.titleOr(к, к)}</th>
                    ))}
                    <th>Правка</th>
                  </tr>
                </thead>
                <tbody>
                  {строки.map((р) => (
                    <tr key={р.id} onClick={() => setОткрытId(р.id === открытId ? null : р.id)}
                        style={{ cursor: 'pointer' }}
                        title="Показать все заполненные поля записи">
                      <td className="mono">{р.id}</td>
                      <td>{fmtText(р.title)}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{fmtText(р.stage_id)}</td>
                      {колонки.map((к) => (
                        <td key={к}>{показатьЗначение(р.raw?.[к], f.meta(к))}</td>
                      ))}
                      <td>{fmtDate(р.updated_time, true)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data && data.next !== null && (
            <div className="pager" style={{ marginTop: 10 }}>
              <button disabled={q.isFetching} onClick={() => setStart(data.next!)}>
                показать ещё {РАЗМЕР_СТРАНИЦЫ}
              </button>
              <span className="muted">
                показано {строки.length} из {pluralN(data.total, 'записи', 'записей', 'записей')}
              </span>
            </div>
          )}
        </div>
      </QueryState>

      {открытId !== null && (
        <КарточкаЗаписи тип={тип} id={открытId} закрыть={() => setОткрытId(null)} />
      )}
    </>
  )
}

/**
 * Карточка-панель: ВСЕ заполненные поля записи из raw. Порядок — как в справочнике
 * полей (sort Bitrix), поля без описания в справочнике — следом, по алфавиту кода:
 * прятать их нельзя, у свежих типов справочник может отставать от зеркала.
 */
function КарточкаЗаписи({ тип, id, закрыть }: { тип: number; id: number; закрыть: () => void }) {
  const f = useFields(`item:${тип}`)
  const q = useЗапись(тип, id)
  const raw = q.data?.row.raw

  const поля = useMemo(() => {
    if (!raw) return []
    const изСправочника = f.list.map((п) => п.code).filter((к) => к in raw)
    const остальные = Object.keys(raw)
      .filter((к) => !изСправочника.includes(к))
      .sort((а, б) => а.localeCompare(б))
    return [...изСправочника, ...остальные].filter((к) => !пусто(raw[к]))
  }, [raw, f])

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Запись {id}</h2>
        <div className="spacer" />
        <button type="button" onClick={закрыть}>закрыть</button>
      </div>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {поля.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Заполненных полей у записи нет</div>
          </div>
        ) : (
          <dl className="dl">
            {поля.map((к) => (
              <div key={к} style={{ display: 'contents' }}>
                <dt title={к}>{f.titleOr(к, к)}</dt>
                <dd>{показатьЗначение(raw?.[к], f.meta(к))}</dd>
              </div>
            ))}
          </dl>
        )}
      </QueryState>
    </div>
  )
}
